import { DollarSign } from "lucide-react";
import { useFormContext } from "react-hook-form";

import FormInput from "@/app/_module/components/FormInput";
import { CurrencyInput } from "@/app/_module/components/CurrencyInput";
import { Form, FormField } from "@/app/_module/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/_module/components/ui/select";

import { employmentStatusOptions, type EmploymentStatus } from "../constants/form-options";
import { type UpdateLeadSchema } from "../schema/lead-schema";

import { FormSection } from "./FormSection";

interface IncomeSectionProps {
  isEditing?: boolean; // Make optional since always editing now
  bare?: boolean; // When true, render fields only without FormSection/Form wrapper
}

export const IncomeSection = function ({ isEditing = true, bare = false }: IncomeSectionProps) {
  const form = useFormContext<UpdateLeadSchema>();

  const {
    control,
    formState: { errors },
    watch,
    setValue,
  } = form;

  const employmentStatus = watch("employmentStatus");

  const fields = (
        <div className="space-y-4">
          <div className="w-full grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <div className="text-sm font-medium text-gray-700">
              Employment Status
            </div>
            <Select
              disabled={!isEditing}
              onValueChange={(value: EmploymentStatus) => setValue("employmentStatus", value)}
              value={employmentStatus || ""}
            >
              <SelectTrigger className={`${errors.employmentStatus ? "border-red-500" : ""} ${!isEditing ? "opacity-60 cursor-not-allowed" : ""}`}>
                <SelectValue placeholder="Select employment status" />
              </SelectTrigger>
              <SelectContent>
                {employmentStatusOptions.map((status) => (
                  <SelectItem key={status} value={status}>
                    {status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!!errors.employmentStatus && <p className="text-sm text-red-600">{errors.employmentStatus.message}</p>}
          </div>

          <FormField
            control={control}
            name="earned"
            render={({ field }) => (
              <CurrencyInput
                containerClass="space-y-2"
                disabled={!isEditing}
                error={errors.earned}
                id="earned"
                label="Annual Earned Income"
                placeholder="$xxx,xxx"
                value={field.value || ""}
                onChange={field.onChange}
                maxAmount={100000000}
                subText="Annual income from employment or business"
                showIcon={false}
              />
            )}
          />
          </div>

          <FormField
            control={control}
            name="worth"
            render={({ field }) => (
              <CurrencyInput
                containerClass="space-y-2"
                disabled={!isEditing}
                error={errors.worth}
                id="worth"
                label="Net Worth"
                placeholder="$xxx,xxx"
                value={field.value || ""}
                onChange={field.onChange}
                maxAmount={100000000}
                subText="Total assets minus total liabilities"
                showIcon={false}
              />
            )}
          />
        </div>
  );

  if (bare) return fields;

  return (
    <FormSection icon={DollarSign} title="Income Information">
      <Form {...form}>
        {fields}
      </Form>
    </FormSection>
  );
};
